/* ----------------------------------------------------------------------------
 * Content Studio v0 — Social data sources. One mock/real gate per fetcher
 * (USE_MOCK_DATA ? mock : api). Real mode targets ONLY the locked BC endpoints
 * (/v1/social/*, /v1/social/brand-kit) — the browser never calls a provider or
 * Pexels directly, and never sees any key.
 *
 * HONESTY INVARIANTS:
 *  - Adapters are null-honest: an absent field stays null/empty, never faked.
 *  - Studio verbs surface the backend's available=false connection-required
 *    state verbatim — no fabricated captions/ideas ever synthesised client-side.
 *  - publish_state is read straight through; 'published' is never inferred.
 * -------------------------------------------------------------------------- */
import { api, apiUpload, USE_MOCK_DATA } from '@/lib/api';
import type { MediaKind } from '@/types/media';
import type {
  AiProvenance,
  AspectKey,
  BrandKit,
  Campaign,
  CampaignList,
  CampaignStatus,
  Channel,
  ChannelSpec,
  ContentItem,
  ContentPage,
  ContentType,
  Format,
  ProviderDirectory,
  ReviewStatus,
  StudioCaption,
  StudioIdeas,
  StudioVisualBrief,
  Template,
  TemplateList,
  VisualBrief,
  CalendarView,
  CampaignChannel,
  CampaignMeta,
  CampaignPreview,
  CampaignPreviewPage,
  CampaignSpecResponse,
} from '@/types/social';

/* ---- P0-A additive type surface (module augmentation; keeps types/social.ts
 *      untouched) --------------------------------------------------------------
 *  - SlotMedia.largeUrl: the HIGHEST saved rendition URL for a page's media,
 *    served additively by the backend alongside the ~600px previewUrl thumbnail
 *    (canva-audit-6). The preview renders largeUrl when present, else previewUrl.
 *  - ContentItem.updatedAt: the row's server updated_at, used as the optimistic-
 *    concurrency baseline the Studio autosave echoes back (base_updated_at). */
declare module '@/types/social' {
  interface SlotMedia {
    largeUrl?: string | null;
  }
  interface ContentItem {
    updatedAt?: string;
  }
}

/* ---- null-honest coercion ------------------------------------------------- */
function strOf(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v !== '' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function clampOverlay(v: unknown): number | undefined {
  const n = numOrNull(v);
  return n === null ? undefined : Math.min(100, Math.max(0, n));
}
function boolOf(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}
function strArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : [];
}
function numArr(v: unknown): number[] {
  return Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];
}

const CHANNELS: Channel[] = ['instagram', 'facebook', 'linkedin', 'x', 'generic'];
function adaptChannel(v: unknown): Channel {
  return CHANNELS.includes(v as Channel) ? (v as Channel) : 'generic';
}
function adaptReview(v: unknown): ReviewStatus {
  return (['draft', 'in_review', 'approved', 'rejected'] as const).includes(v as ReviewStatus)
    ? (v as ReviewStatus)
    : 'draft';
}
function adaptContentType(v: unknown): ContentType {
  return (['post', 'caption', 'idea', 'hook'] as const).includes(v as ContentType) ? (v as ContentType) : 'post';
}

/* ---- visual-brief (studio composition) blob ------------------------------- */
function adaptPage(raw: unknown, idx: number): ContentPage {
  const o = (raw ?? {}) as Record<string, unknown>;
  const slotsRaw = (o.slots ?? {}) as Record<string, unknown>;
  const mediaRaw = slotsRaw.media as Record<string, unknown> | null | undefined;
  return {
    idx: numOrNull(o.idx) ?? idx,
    layoutKey: strOf(o.layoutKey, 'text-card'),
    align: (['start', 'center', 'end'] as const).includes(o.align as never) ? (o.align as ContentPage['align']) : 'start',
    textSize: (['s', 'm', 'l'] as const).includes(o.textSize as never) ? (o.textSize as ContentPage['textSize']) : 'm',
    slots: {
      headline: strOrNull(slotsRaw.headline) ?? undefined,
      body: strOrNull(slotsRaw.body) ?? undefined,
      cta: strOrNull(slotsRaw.cta) ?? undefined,
      hashtags: strArr(slotsRaw.hashtags),
      logo: boolOf(slotsRaw.logo),
      contact: boolOf(slotsRaw.contact),
      overlay: clampOverlay(slotsRaw.overlay),
      media: mediaRaw
        ? {
            assetId: numOrNull(mediaRaw.assetId),
            kind: (mediaRaw.kind === 'video' ? 'video' : 'photo') as MediaKind,
            attribution: strOf(mediaRaw.attribution),
            previewUrl: strOrNull(mediaRaw.previewUrl),
            // P0-A: HIGHEST saved rendition (canva-audit-6); null-honest fallback.
            largeUrl: strOrNull(mediaRaw.largeUrl),
          }
        : null,
    },
  };
}
function adaptVisualBrief(raw: unknown): VisualBrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const pages = Array.isArray(o.pages) ? o.pages.map(adaptPage) : [];
  return {
    brief: strOf(o.brief),
    stylePreset: strOf(o.stylePreset),
    aspect: (['landscape', 'portrait', 'square'] as const).includes(o.aspect as AspectKey)
      ? (o.aspect as AspectKey)
      : 'square',
    imagePrompt: strOf(o.imagePrompt),
    format: (o.format === 'carousel' ? 'carousel' : 'single') as Format,
    pages,
  };
}

/* ---- content adapters ----------------------------------------------------- */
interface ContentDto {
  id?: number | string;
  campaign_id?: number | string | null;
  channel?: string;
  content_type?: string;
  title?: string;
  body_text?: string;
  hooks?: unknown;
  visual_brief?: unknown;
  layout_key?: string;
  template_id?: string;
  review_status?: string;
  review_note?: string;
  ai_provenance?: Record<string, unknown> | null;
  approved_by?: string | null;
  approved_at?: string | null;
  scheduled_for?: string | null;
  publish_state?: string;
  provider_ref?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Per-row domain attribution (rule 3): the ORIGINATING domain of the row,
   *  resolved server-side from its proven project_id. Additive + null-honest. */
  domain?: { project_id?: number | string | null; host?: string; label?: string; unassigned?: boolean } | null;
}
function adaptContent(dto: ContentDto): ContentItem {
  const prov = dto.ai_provenance && typeof dto.ai_provenance === 'object' ? dto.ai_provenance : null;
  return {
    id: numOrNull(dto.id) ?? 0,
    campaignId: numOrNull(dto.campaign_id),
    channel: adaptChannel(dto.channel),
    contentType: adaptContentType(dto.content_type),
    title: strOf(dto.title),
    bodyText: strOf(dto.body_text),
    hooks: strArr(dto.hooks),
    visualBrief: adaptVisualBrief(dto.visual_brief),
    layoutKey: strOf(dto.layout_key),
    templateId: strOf(dto.template_id),
    reviewStatus: adaptReview(dto.review_status),
    reviewNote: strOf(dto.review_note),
    aiProvenance: prov
      ? {
          capability: strOf(prov.capability),
          model: strOf(prov.model),
          prompt_version: strOf(prov.prompt_version),
          generated_at: strOf(prov.generated_at),
        }
      : null,
    approvedBy: strOrNull(dto.approved_by),
    approvedAt: strOrNull(dto.approved_at),
    scheduledFor: strOrNull(dto.scheduled_for),
    publishState: strOf(dto.publish_state, 'none') as ContentItem['publishState'],
    providerRef: strOrNull(dto.provider_ref),
    createdAt: strOf(dto.created_at),
    updatedAt: strOrNull(dto.updated_at) ?? undefined,
  };
}

/* ---- per-row domain attribution (P0 Recent-Content All-domains lens) --------
 * The backend labels every content row with its ORIGINATING domain (rule 3): the
 * proven project_id resolved to a host + display label, or Unassigned. The FE
 * uses it to (a) label each card in the "All domains" view and (b) restore the
 * draft's OWN domain context when opening it (rule 4) -- never the domain that
 * happened to be selected while discovering it. Additive + null-honest: an
 * absent/blank attribution degrades to Unassigned, never a fabricated domain. */
export interface ContentDomainAttribution {
  /** the row's own proven project_id, or null when Unassigned. */
  projectId: number | null;
  /** resolved hostname (may be empty when only a label is known). */
  host: string;
  /** human label for the badge (display name / host / "Unassigned"). */
  label: string;
  unassigned: boolean;
}
export interface RecentContentItem extends ContentItem {
  domain: ContentDomainAttribution;
}
export interface RecentContentList {
  items: RecentContentItem[];
  nextCursor: string | null;
  source: string;
}
function adaptContentDomain(v: unknown): ContentDomainAttribution {
  const d = v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  const pid = numOrNull(d.project_id);
  const unassigned = d.unassigned === true || pid === null || pid <= 0;
  return {
    projectId: unassigned ? null : pid,
    host: strOf(d.host),
    label: strOf(d.label, unassigned ? 'Unassigned' : 'Domain'),
    unassigned,
  };
}
function adaptRecentContent(dto: ContentDto): RecentContentItem {
  return { ...adaptContent(dto), domain: adaptContentDomain(dto.domain) };
}
/** Dev-only: give a mock item a neutral (Unassigned) attribution so the mock
 *  list satisfies RecentContentList without inventing a domain. */
function withMockDomain(c: ContentItem): RecentContentItem {
  return { ...c, domain: { projectId: null, host: '', label: 'Unassigned', unassigned: true } };
}

/** Payload for POST/PATCH content — snake_case for the API. */
export interface ContentDraftInput {
  channel: Channel;
  contentType?: ContentType;
  title?: string;
  bodyText?: string;
  hooks?: string[];
  visualBrief?: VisualBrief | null;
  layoutKey?: string;
  templateId?: string;
  campaignId?: number | null;
  /** Rule 5: the selected 'Viewing' domain a NEW draft inherits (server re-
   *  validated for tenancy; a foreign/absent id leaves the draft Unassigned). */
  projectId?: number | null;
  aiProvenance?: { capability: string; model: string; prompt_version: string; generated_at: string } | null;
}
function toContentBody(input: ContentDraftInput): Record<string, unknown> {
  const body: Record<string, unknown> = { channel: input.channel };
  if (input.contentType !== undefined) body.content_type = input.contentType;
  if (input.title !== undefined) body.title = input.title;
  if (input.bodyText !== undefined) body.body_text = input.bodyText;
  if (input.hooks !== undefined) body.hooks = input.hooks;
  if (input.visualBrief !== undefined) body.visual_brief = input.visualBrief;
  if (input.layoutKey !== undefined) body.layout_key = input.layoutKey;
  if (input.templateId !== undefined) body.template_id = input.templateId;
  if (input.campaignId !== undefined && input.campaignId !== null) body.campaign_id = input.campaignId;
  if (typeof input.projectId === 'number' && input.projectId > 0) body.project_id = input.projectId;
  if (input.aiProvenance) body.ai_provenance = input.aiProvenance;
  return body;
}

/* ---- MOCK store (dev-only, STRICTLY behind USE_MOCK_DATA) ------------------ */
let MOCK_CONTENT: ContentItem[] = [];
let MOCK_CAMPAIGNS: Campaign[] = [];
let MOCK_TEMPLATES: Template[] = [];
let MOCK_BRAND: BrandKit = {
  tone: '', audience: '', voice: '', bannedPhrases: [], sampleCopy: '', defaultCta: '', contact: '',
  colorPrimary: '#A5622E', colorSecondary: '#1B1B1A', fonts: ['Source Serif 4', 'Inter'],
  logoAssetId: null, approvedAssetIds: [], source: 'mock',
};
let mockSeq = 700;

/* ---- content fetchers ----------------------------------------------------- */
export async function listContent(campaignId?: number | null, projectId?: number | null): Promise<RecentContentList> {
  if (USE_MOCK_DATA) {
    const items = campaignId ? MOCK_CONTENT.filter((c) => c.campaignId === campaignId) : MOCK_CONTENT;
    return { items: items.map(withMockDomain), nextCursor: null, source: 'bc_social_content_items' };
  }
  // Domain isolation (rules 1,6): when a 'Viewing' domain is active, scope the
  // list to it (project_id); no domain selected -> omit it for the honest
  // "Across workspace / Unassigned" view. projectId is threaded into the
  // queryKey by callers so switching Viewing refetches + drops the stale list.
  const res = await api.get<{ items?: ContentDto[]; next_cursor?: string | null; source?: string }>('/social/content', {
    campaign_id: campaignId ?? undefined,
    project_id: typeof projectId === 'number' && projectId > 0 ? projectId : undefined,
    limit: 100,
  });
  return {
    items: (res.items ?? []).map(adaptRecentContent),
    nextCursor: strOrNull(res.next_cursor),
    source: strOf(res.source, 'bc_social_content_items'),
  };
}

export async function getContent(id: number): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    const found = MOCK_CONTENT.find((c) => c.id === id);
    if (!found) throw new Error('content_not_found');
    return found;
  }
  const res = await api.get<ContentDto>(`/social/content/${id}`);
  return adaptContent(res);
}

export async function createContent(input: ContentDraftInput): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    mockSeq += 1;
    const item: ContentItem = {
      id: mockSeq, campaignId: input.campaignId ?? null, channel: input.channel,
      contentType: input.contentType ?? 'post', title: input.title ?? '', bodyText: input.bodyText ?? '',
      hooks: input.hooks ?? [], visualBrief: input.visualBrief ?? null, layoutKey: input.layoutKey ?? '',
      templateId: input.templateId ?? '', reviewStatus: 'draft', reviewNote: '',
      aiProvenance: input.aiProvenance ?? null, approvedBy: null, approvedAt: null, scheduledFor: null,
      publishState: 'none', providerRef: null, createdAt: new Date().toISOString(),
    };
    MOCK_CONTENT = [item, ...MOCK_CONTENT];
    return item;
  }
  const res = await api.post<{ item?: ContentDto }>('/social/content', toContentBody(input));
  return adaptContent(res.item ?? {});
}

export async function updateContent(id: number, input: Partial<ContentDraftInput>): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.map((c) =>
      c.id === id
        ? {
            ...c,
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.bodyText !== undefined ? { bodyText: input.bodyText } : {}),
            ...(input.channel !== undefined ? { channel: input.channel } : {}),
            ...(input.hooks !== undefined ? { hooks: input.hooks } : {}),
            ...(input.visualBrief !== undefined ? { visualBrief: input.visualBrief } : {}),
            ...(input.layoutKey !== undefined ? { layoutKey: input.layoutKey } : {}),
            ...(input.templateId !== undefined ? { templateId: input.templateId } : {}),
            reviewStatus: c.reviewStatus === 'approved' || c.reviewStatus === 'rejected' ? 'draft' : c.reviewStatus,
          }
        : c,
    );
    return getContent(id);
  }
  const res = await api.patch<{ item?: ContentDto }>(`/social/content/${id}`, toContentBody({ channel: 'generic', ...input } as ContentDraftInput));
  return adaptContent(res.item ?? {});
}

/** Payload for a Studio AUTOSAVE (P0-A). Composition-only + approval-preserving:
 *  the backend routes this through the SAFE saveComposition path (never the
 *  review-reopening update path). `baseUpdatedAt` is the optimistic-concurrency
 *  baseline — a stale value is refused server-side with 409 (ApiError.status). */
export interface CompositionAutosaveInput {
  title?: string;
  bodyText?: string;
  layoutKey?: string;
  hooks?: string[];
  visualBrief?: VisualBrief | null;
  baseUpdatedAt?: string | null;
}

/** Autosave the editable Studio composition WITHOUT re-opening review. Sends the
 *  `composition_only` flag so an APPROVED draft stays approved (canva-audit-5);
 *  echoes baseUpdatedAt so the server can reject a stale overwrite (409). */
export async function autosaveComposition(id: number, input: CompositionAutosaveInput): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    // Dev mock: composition-only save PRESERVES review_status (never re-opens).
    MOCK_CONTENT = MOCK_CONTENT.map((c) =>
      c.id === id
        ? {
            ...c,
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.bodyText !== undefined ? { bodyText: input.bodyText } : {}),
            ...(input.layoutKey !== undefined ? { layoutKey: input.layoutKey } : {}),
            ...(input.hooks !== undefined ? { hooks: input.hooks } : {}),
            ...(input.visualBrief !== undefined ? { visualBrief: input.visualBrief } : {}),
            // review_status / approvedBy / approvedAt intentionally preserved.
          }
        : c,
    );
    return getContent(id);
  }
  const body: Record<string, unknown> = { composition_only: true };
  if (input.title !== undefined) body.title = input.title;
  if (input.bodyText !== undefined) body.body_text = input.bodyText;
  if (input.layoutKey !== undefined) body.layout_key = input.layoutKey;
  if (input.hooks !== undefined) body.hooks = input.hooks;
  if (input.visualBrief !== undefined) body.visual_brief = input.visualBrief;
  if (typeof input.baseUpdatedAt === 'string' && input.baseUpdatedAt !== '') {
    body.base_updated_at = input.baseUpdatedAt;
  }
  const res = await api.patch<{ item?: ContentDto }>(`/social/content/${id}`, body);
  return adaptContent(res.item ?? {});
}

async function contentAction(
  id: number,
  path: string,
  method: 'post' | 'patch',
  body: Record<string, unknown> = {},
): Promise<ContentItem> {
  if (USE_MOCK_DATA) return getContent(id);
  const call = method === 'post' ? api.post : api.patch;
  const res = await call<{ item?: ContentDto }>(`/social/content/${id}${path}`, body);
  return adaptContent(res.item ?? {});
}

export function submitReview(id: number): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.map((c) => (c.id === id ? { ...c, reviewStatus: 'in_review' } : c));
    return getContent(id);
  }
  return contentAction(id, '/submit-review', 'post');
}
/** HUMAN-ONLY on the server (owner directive): a machine key is refused 403. */
export function approveContent(id: number): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.map((c) => (c.id === id ? { ...c, reviewStatus: 'approved', approvedBy: 'human:mock' } : c));
    return getContent(id);
  }
  return contentAction(id, '/approve', 'post');
}
export function rejectContent(id: number, note: string): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.map((c) => (c.id === id ? { ...c, reviewStatus: 'rejected', reviewNote: note } : c));
    return getContent(id);
  }
  return contentAction(id, '/reject', 'post', { note });
}
/** Calendar placement — server drives publish_state to connection_required. */
export function scheduleContent(id: number, scheduledFor: string | null): Promise<ContentItem> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.map((c) =>
      c.id === id
        ? { ...c, scheduledFor, publishState: scheduledFor ? 'connection_required' : 'none' }
        : c,
    );
    return getContent(id);
  }
  return contentAction(id, '/schedule', 'patch', { scheduled_for: scheduledFor });
}

/** Soft-delete a DRAFT. Server enforces human-only + drafts-only + workspace
 *  scope and tombstones the row (not a physical delete) — it then disappears
 *  from the list and calendar. Returns void; the caller reconciles the list. */
export async function deleteContent(id: number): Promise<void> {
  if (USE_MOCK_DATA) {
    MOCK_CONTENT = MOCK_CONTENT.filter((c) => c.id !== id);
    return;
  }
  await api.del<unknown>(`/social/content/${id}`);
}

/* ---- campaigns ------------------------------------------------------------ */
interface CampaignDto {
  id?: number | string; name?: string; theme?: string; objective?: string; status?: string;
  starts_on?: string | null; ends_on?: string | null; created_at?: string;
}
function adaptCampaign(dto: CampaignDto): Campaign {
  return {
    id: numOrNull(dto.id) ?? 0, name: strOf(dto.name), theme: strOf(dto.theme), objective: strOf(dto.objective),
    status: (['draft', 'active', 'completed', 'archived'] as const).includes(dto.status as CampaignStatus)
      ? (dto.status as CampaignStatus) : 'draft',
    startsOn: strOrNull(dto.starts_on), endsOn: strOrNull(dto.ends_on), createdAt: strOf(dto.created_at),
  };
}
export async function listCampaigns(): Promise<CampaignList> {
  if (USE_MOCK_DATA) return { items: [...MOCK_CAMPAIGNS], nextCursor: null, source: 'bc_social_campaigns' };
  const res = await api.get<{ campaigns?: CampaignDto[]; next_cursor?: string | null; source?: string }>('/social/campaigns', { limit: 100 });
  return { items: (res.campaigns ?? []).map(adaptCampaign), nextCursor: strOrNull(res.next_cursor), source: strOf(res.source, 'bc_social_campaigns') };
}
export async function createCampaign(input: { name: string; theme?: string; objective?: string }): Promise<Campaign> {
  if (USE_MOCK_DATA) {
    mockSeq += 1;
    const c: Campaign = { id: mockSeq, name: input.name, theme: input.theme ?? '', objective: input.objective ?? '', status: 'draft', startsOn: null, endsOn: null, createdAt: new Date().toISOString() };
    MOCK_CAMPAIGNS = [c, ...MOCK_CAMPAIGNS];
    return c;
  }
  const res = await api.post<{ campaign?: CampaignDto }>('/social/campaigns', { name: input.name, theme: input.theme, objective: input.objective });
  return adaptCampaign(res.campaign ?? {});
}

/* ---- templates ------------------------------------------------------------ */
interface TemplateDto {
  id?: number | string; name?: string; channel?: string | null; layout_key?: string;
  structure?: unknown; body_template?: string; created_at?: string;
}
function adaptTemplate(dto: TemplateDto): Template {
  const s = dto.structure && typeof dto.structure === 'object' ? (dto.structure as Record<string, unknown>) : null;
  return {
    id: numOrNull(dto.id) ?? 0, name: strOf(dto.name),
    channel: dto.channel ? adaptChannel(dto.channel) : null, layoutKey: strOf(dto.layout_key),
    structure: s
      ? {
          layoutKeys: strArr(s.layoutKeys),
          defaultAspect: (['landscape', 'portrait', 'square'] as const).includes(s.defaultAspect as AspectKey) ? (s.defaultAspect as AspectKey) : 'square',
          brandBindings: {
            useLogo: boolOf((s.brandBindings as Record<string, unknown>)?.useLogo),
            colorRoles: { bg: 'neutral', accent: 'primary' },
            fontRole: strOf((s.brandBindings as Record<string, unknown>)?.fontRole, 'Source Serif 4'),
          },
        }
      : null,
    bodyTemplate: strOf(dto.body_template), createdAt: strOf(dto.created_at),
  };
}
export async function listTemplates(): Promise<TemplateList> {
  if (USE_MOCK_DATA) return { items: [...MOCK_TEMPLATES], nextCursor: null, source: 'bc_social_templates' };
  const res = await api.get<{ templates?: TemplateDto[]; next_cursor?: string | null; source?: string }>('/social/templates', { limit: 100 });
  return { items: (res.templates ?? []).map(adaptTemplate), nextCursor: strOrNull(res.next_cursor), source: strOf(res.source, 'bc_social_templates') };
}
export async function createContentFromTemplate(template: Template): Promise<ContentItem> {
  // "Use Template" creates a NEW editable draft populated from the template's
  // pages/layout/copy/brand bindings. It NEVER mutates the template (no PATCH to
  // /social/templates) — it only POSTs a fresh content item, so the original is
  // preserved and reusable. Media SLOTS stay empty (the user picks media in the
  // Media step); brand colours/fonts/logo resolve live at render from Brand Kit.
  const layoutKeys = template.structure?.layoutKeys?.length
    ? template.structure.layoutKeys
    : [template.layoutKey || 'hero-media'];
  const aspect: AspectKey = template.structure?.defaultAspect ?? 'square';
  const useLogo = template.structure?.brandBindings?.useLogo ?? true;
  const pages: ContentPage[] = layoutKeys.map((lk, idx) => ({
    idx,
    layoutKey: lk || 'hero-media',
    align: 'start',
    textSize: 'm',
    slots: {
      logo: useLogo,
      ...(idx === 0 && template.bodyTemplate ? { body: template.bodyTemplate } : {}),
    },
  }));
  const visualBrief: VisualBrief = {
    brief: '',
    stylePreset: '',
    aspect,
    imagePrompt: '',
    format: pages.length > 1 ? 'carousel' : 'single',
    pages,
  };
  return createContent({
    channel: template.channel ?? 'generic',
    contentType: 'post',
    title: template.name,
    bodyText: template.bodyTemplate || '',
    layoutKey: pages[0].layoutKey,
    templateId: String(template.id),
    visualBrief,
  });
}
export async function createTemplate(input: {
  name: string; channel: Channel | null; layoutKey: string; structure: Record<string, unknown>; bodyTemplate: string;
}): Promise<Template> {
  if (USE_MOCK_DATA) {
    mockSeq += 1;
    const t = adaptTemplate({ id: mockSeq, name: input.name, channel: input.channel, layout_key: input.layoutKey, structure: input.structure, body_template: input.bodyTemplate, created_at: new Date().toISOString() });
    MOCK_TEMPLATES = [t, ...MOCK_TEMPLATES];
    return t;
  }
  const res = await api.post<{ template?: TemplateDto }>('/social/templates', {
    name: input.name, channel: input.channel ?? undefined, layout_key: input.layoutKey,
    structure: input.structure, body_template: input.bodyTemplate,
  });
  return adaptTemplate(res.template ?? {});
}

/* ---- studio capabilities (honest connection-required passthrough) --------- */
export async function studioIdeas(input: { channel: Channel; topic: string; goal?: string; campaignTheme?: string; campaignObjective?: string }): Promise<StudioIdeas> {
  if (USE_MOCK_DATA) return { available: false, capability: 'social_content_ideas', state: 'connection_required', message: 'AI Studio requires the AI service to be enabled (dev mock).' };
  const res = await api.post<Record<string, unknown>>('/social/studio/ideas', {
    channel: input.channel, topic: input.topic, goal: input.goal, campaign_theme: input.campaignTheme, campaign_objective: input.campaignObjective,
  });
  if (!boolOf(res.available)) return { available: false, capability: strOf(res.capability, 'social_content_ideas'), state: 'connection_required', message: strOf(res.message, 'AI Studio connection required.') };
  return { available: true, capability: strOf(res.capability, 'social_content_ideas'), ideas: strArr(res.ideas), hooks: strArr(res.hooks), aiProvenance: adaptProv(res.ai_provenance) };
}
export async function studioCaption(input: { channel: Channel; topic: string; keyPoints?: string; cta?: string; campaignTheme?: string; campaignObjective?: string }): Promise<StudioCaption> {
  if (USE_MOCK_DATA) return { available: false, capability: 'social_caption_draft', state: 'connection_required', message: 'AI Studio requires the AI service to be enabled (dev mock).' };
  const res = await api.post<Record<string, unknown>>('/social/studio/caption', {
    channel: input.channel, topic: input.topic, key_points: input.keyPoints, cta: input.cta, campaign_theme: input.campaignTheme, campaign_objective: input.campaignObjective,
  });
  if (!boolOf(res.available)) return { available: false, capability: strOf(res.capability, 'social_caption_draft'), state: 'connection_required', message: strOf(res.message, 'AI Studio connection required.') };
  return { available: true, capability: strOf(res.capability, 'social_caption_draft'), channel: adaptChannel(res.channel), caption: strOf(res.caption), headline: strOf(res.headline), valid: boolOf(res.valid), violations: strArr(res.violations), flags: strArr(res.flags), aiProvenance: adaptProv(res.ai_provenance) };
}
export async function studioVisualBrief(input: { channel: Channel; topic: string; mood?: string; campaignTheme?: string; campaignObjective?: string }): Promise<StudioVisualBrief> {
  if (USE_MOCK_DATA) return { available: false, capability: 'social_visual_brief', state: 'connection_required', message: 'AI Studio requires the AI service to be enabled (dev mock).' };
  const res = await api.post<Record<string, unknown>>('/social/studio/visual-brief', {
    channel: input.channel, topic: input.topic, mood: input.mood, campaign_theme: input.campaignTheme, campaign_objective: input.campaignObjective,
  });
  if (!boolOf(res.available)) return { available: false, capability: strOf(res.capability, 'social_visual_brief'), state: 'connection_required', message: strOf(res.message, 'AI Studio connection required.') };
  return { available: true, capability: strOf(res.capability, 'social_visual_brief'), visualBrief: strOf(res.visual_brief), imagePrompt: strOf(res.image_prompt), imageGeneration: strOf(res.image_generation, 'not_supported'), aiProvenance: adaptProv(res.ai_provenance) };
}
function adaptProv(v: unknown): ContentItem['aiProvenance'] {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  return { capability: strOf(o.capability), model: strOf(o.model), prompt_version: strOf(o.prompt_version), generated_at: strOf(o.generated_at, new Date().toISOString()) };
}

/* ---- providers (honest connection states) --------------------------------- */
function adaptSpec(v: unknown): ChannelSpec {
  const o = (v ?? {}) as Record<string, unknown>;
  return { maxChars: numOrNull(o.max_chars) ?? 5000, maxMedia: numOrNull(o.max_media) ?? 10, mediaTypes: strArr(o.media_types), hashtagsMax: numOrNull(o.hashtags_max) ?? 30 };
}
export async function listProviders(): Promise<ProviderDirectory> {
  if (USE_MOCK_DATA) {
    return {
      providers: CHANNELS.filter((c) => c !== 'generic').map((c) => ({ provider: c, connectionState: 'not_connected', connectable: false, channelSpec: { maxChars: 2200, maxMedia: 10, mediaTypes: ['image', 'video'], hashtagsMax: 30 } })),
      note: 'No provider is connected (dev mock). Publishing requires owner-provisioned credentials (G-SOC-3).',
      source: 'social.provider_registry',
    };
  }
  const res = await api.get<{ providers?: Array<Record<string, unknown>>; note?: string; source?: string }>('/social/providers');
  return {
    providers: (res.providers ?? []).map((p) => ({ provider: adaptChannel(p.provider), connectionState: (['not_connected', 'pending', 'connected', 'error'] as const).includes(p.connection_state as never) ? (p.connection_state as never) : 'not_connected', connectable: boolOf(p.connectable), channelSpec: adaptSpec(p.channel_spec) })),
    note: strOf(res.note), source: strOf(res.source, 'social.provider_registry'),
  };
}
/** Per-channel constraints keyed by channel — for live preview char counts. */
export async function channelSpecs(): Promise<Record<Channel, ChannelSpec>> {
  const dir = await listProviders();
  const out = {} as Record<Channel, ChannelSpec>;
  for (const p of dir.providers) out[p.provider] = p.channelSpec;
  if (!out.generic) out.generic = { maxChars: 5000, maxMedia: 10, mediaTypes: ['image', 'video'], hashtagsMax: 30 };
  return out;
}

/* ---- calendar ------------------------------------------------------------- */
export async function fetchCalendar(from?: string, to?: string, projectId?: number | null): Promise<CalendarView> {
  if (USE_MOCK_DATA) {
    const entries = MOCK_CONTENT.filter((c) => c.scheduledFor).map((c) => ({
      id: c.id, title: c.title, channel: c.channel, contentType: c.contentType, reviewStatus: c.reviewStatus,
      scheduledFor: c.scheduledFor as string, publishState: c.publishState,
      publishLabel: 'Scheduled locally — connection required to publish.',
    }));
    return { entries, count: entries.length, from: from ?? null, to: to ?? null, source: 'bc_social_content_items.scheduled_for' };
  }
  const res = await api.get<{ entries?: Array<Record<string, unknown>>; count?: number; from?: string | null; to?: string | null; source?: string }>('/social/calendar', {
    from,
    to,
    // Domain isolation (rules 1,6): scope the calendar to the active 'Viewing'
    // domain; omit when none is selected ("Across workspace" view).
    project_id: typeof projectId === 'number' && projectId > 0 ? projectId : undefined,
  });
  return {
    entries: (res.entries ?? []).map((e) => ({ id: numOrNull(e.id) ?? 0, title: strOf(e.title), channel: adaptChannel(e.channel), contentType: adaptContentType(e.content_type), reviewStatus: adaptReview(e.review_status), scheduledFor: strOf(e.scheduled_for), publishState: strOf(e.publish_state, 'none') as ContentItem['publishState'], publishLabel: strOf(e.publish_label) })),
    count: numOrNull(res.count) ?? 0, from: strOrNull(res.from), to: strOrNull(res.to), source: strOf(res.source, 'bc_social_content_items.scheduled_for'),
  };
}

/* ---- brand kit ------------------------------------------------------------ */
function adaptBrand(v: Record<string, unknown>): BrandKit {
  return {
    tone: strOf(v.tone), audience: strOf(v.audience), voice: strOf(v.voice),
    bannedPhrases: strArr(v.banned_phrases), sampleCopy: strOf(v.sample_copy),
    defaultCta: strOf(v.default_cta), contact: strOf(v.contact),
    colorPrimary: strOf(v.color_primary), colorSecondary: strOf(v.color_secondary),
    fonts: strArr(v.fonts), logoAssetId: numOrNull(v.logo_asset_id), approvedAssetIds: numArr(v.approved_asset_ids),
    source: strOf(v.source, 'bc_workspace_settings:social.brand.*'),
  };
}
export async function getBrandKit(): Promise<BrandKit> {
  if (USE_MOCK_DATA) return { ...MOCK_BRAND };
  const res = await api.get<Record<string, unknown>>('/social/brand-kit');
  return adaptBrand(res);
}
export async function saveBrandKit(input: Partial<BrandKit>): Promise<BrandKit> {
  if (USE_MOCK_DATA) {
    MOCK_BRAND = { ...MOCK_BRAND, ...input };
    return { ...MOCK_BRAND };
  }
  const body: Record<string, unknown> = {};
  if (input.tone !== undefined) body.tone = input.tone;
  if (input.audience !== undefined) body.audience = input.audience;
  if (input.voice !== undefined) body.voice = input.voice;
  if (input.bannedPhrases !== undefined) body.banned_phrases = input.bannedPhrases;
  if (input.sampleCopy !== undefined) body.sample_copy = input.sampleCopy;
  if (input.defaultCta !== undefined) body.default_cta = input.defaultCta;
  if (input.contact !== undefined) body.contact = input.contact;
  if (input.colorPrimary !== undefined) body.color_primary = input.colorPrimary;
  if (input.colorSecondary !== undefined) body.color_secondary = input.colorSecondary;
  if (input.fonts !== undefined) body.fonts = input.fonts;
  if (input.logoAssetId !== undefined) body.logo_asset_id = input.logoAssetId;
  if (input.approvedAssetIds !== undefined) body.approved_asset_ids = input.approvedAssetIds;
  const res = await api.patch<Record<string, unknown>>('/social/brand-kit', body);
  return adaptBrand(res);
}

/* ---- Brand-Kit LOGO (direct upload + tenant-scoped serving) --------------- */

export interface BrandLogo {
  assetId: number;
  mime: string;
  width: number | null;
  height: number | null;
  bytes: number;
  dataUrl: string;
}

function adaptLogo(v: Record<string, unknown>): BrandLogo {
  return {
    assetId: numOrNull(v.asset_id) ?? 0,
    mime: strOf(v.mime),
    width: numOrNull(v.width),
    height: numOrNull(v.height),
    bytes: numOrNull(v.bytes) ?? 0,
    dataUrl: strOf(v.data_url),
  };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

let MOCK_LOGO: BrandLogo | null = null;

/** Serve the current workspace's logo, or null when none is set. */
export async function getBrandLogo(): Promise<BrandLogo | null> {
  if (USE_MOCK_DATA) return MOCK_LOGO;
  const res = await api.get<{ present: boolean; logo: Record<string, unknown> | null }>('/social/brand-kit/logo');
  return res.present && res.logo ? adaptLogo(res.logo) : null;
}

/** Directly upload a PNG/JPEG logo (raw bytes) and set it as the kit logo. */
export async function uploadBrandLogo(file: File): Promise<BrandLogo> {
  if (USE_MOCK_DATA) {
    const dataUrl = await fileToDataUrl(file);
    MOCK_LOGO = { assetId: 1, mime: file.type, width: null, height: null, bytes: file.size, dataUrl };
    MOCK_BRAND = { ...MOCK_BRAND, logoAssetId: 1 };
    return MOCK_LOGO;
  }
  const res = await apiUpload<{ logo: Record<string, unknown> }>(
    '/social/brand-kit/logo',
    file,
    file.type || 'application/octet-stream',
  );
  return adaptLogo(res.logo ?? {});
}

/* ---- Prompt-to-Campaign AI Creator (honest passthrough) -------------------
 * POST /v1/social/studio/campaign — advisory, persists NOTHING. Honesty
 * invariants: available=false -> connection_required verbatim; valid=false ->
 * insufficient_signal/invalid_request verbatim; only what the API returns is
 * rendered (no client-side synthesis). The platform idempotency header is
 * added automatically by apiRequest() on every POST, so a retry replays the
 * prior generation instead of paying for a second dispatch.
 * -------------------------------------------------------------------------- */
const CAMPAIGN_CHANNELS: CampaignChannel[] = ['instagram', 'facebook', 'linkedin', 'x', 'generic', 'blog', 'email'];
function adaptCampaignChannel(v: unknown): CampaignChannel {
  return CAMPAIGN_CHANNELS.includes(v as CampaignChannel) ? (v as CampaignChannel) : 'generic';
}
function adaptCampaignMeta(v: unknown): CampaignMeta {
  const o = (v ?? {}) as Record<string, unknown>;
  return { name: strOf(o.name), theme: strOf(o.theme), objective: strOf(o.objective) };
}
function adaptCampaignPage(v: unknown): CampaignPreviewPage {
  const o = (v ?? {}) as Record<string, unknown>;
  return { heading: strOf(o.heading), body: strOf(o.body) };
}
function adaptCampaignPreview(v: unknown): CampaignPreview {
  const o = (v ?? {}) as Record<string, unknown>;
  return {
    channel: adaptCampaignChannel(o.channel),
    contentType: strOf(o.content_type, 'post'),
    aspectRatio: strOrNull(o.aspect_ratio),
    layoutKey: strOf(o.layout_key),
    title: strOf(o.title),
    body: strOf(o.body),
    hooks: strArr(o.hooks),
    ctas: strArr(o.ctas),
    hashtags: strArr(o.hashtags),
    visualDirection: strOf(o.visual_direction),
    stockMediaQueries: strArr(o.stock_media_queries),
    imageGeneration: strOf(o.image_generation, 'not_supported'),
    pages: Array.isArray(o.pages) ? o.pages.map(adaptCampaignPage) : [],
    acceptAs: strOf(o.accept_as, 'content_draft'),
  };
}

export async function generateCampaign(input: {
  prompt: string;
  channels: string[];
  campaignTheme?: string;
  campaignObjective?: string;
}): Promise<CampaignSpecResponse> {
  if (USE_MOCK_DATA) {
    return {
      available: false,
      valid: false,
      state: 'connection_required',
      message: 'The Campaign Creator requires the AI service to be enabled (dev mock). No content was generated.',
    };
  }
  const res = await api.post<Record<string, unknown>>('/social/studio/campaign', {
    prompt: input.prompt,
    channels: input.channels,
    campaign_theme: input.campaignTheme,
    campaign_objective: input.campaignObjective,
  });
  if (!boolOf(res.available)) {
    return {
      available: false,
      valid: false,
      state: 'connection_required',
      message: strOf(res.message, 'The Campaign Creator requires the AI service to be enabled. No content was generated.'),
    };
  }
  if (!boolOf(res.valid)) {
    return {
      available: true,
      valid: false,
      state: res.state === 'invalid_request' ? 'invalid_request' : 'insufficient_signal',
      message: strOf(res.message, 'The campaign could not be generated from this prompt. No content was produced.'),
      campaign: res.campaign ? adaptCampaignMeta(res.campaign) : null,
      dropped: strArr(res.dropped),
    };
  }
  return {
    available: true,
    valid: true,
    campaign: adaptCampaignMeta(res.campaign),
    previews: Array.isArray(res.previews) ? res.previews.map(adaptCampaignPreview) : [],
    dropped: strArr(res.dropped),
    aiProvenance: adaptProv(res.ai_provenance),
  };
}

/* Map the wider CampaignChannel down to a persistable publish Channel for the
 * existing content-draft path (blog/email fold to 'generic'; the channel intent
 * is preserved in the title/visual brief). */
function campaignChannelToPublish(ch: CampaignChannel): Channel {
  return (['instagram', 'facebook', 'linkedin', 'x', 'generic'] as const).includes(ch as never)
    ? (ch as Channel)
    : 'generic';
}
/** Preview aspect_ratio ('4:5'|'1.91:1'|null) -> studio AspectKey. */
function campaignAspectToKey(ratio: string | null): AspectKey {
  if (ratio === '4:5') return 'portrait';
  if (ratio === '1.91:1' || ratio === '16:9') return 'landscape';
  return 'square';
}

/**
 * Decompose one approval-ready preview into the EXISTING content-draft input.
 * Nothing is invented: every field comes straight from the validated preview.
 * The human still reviews/edits and approves via the unchanged content path.
 */
export function campaignPreviewToDraft(preview: CampaignPreview, provenance: AiProvenance | null): ContentDraftInput {
  const format: Format = preview.contentType === 'carousel' ? 'carousel' : 'single';
  const aspect = campaignAspectToKey(preview.aspectRatio);
  const pages: ContentPage[] =
    preview.pages.length > 0
      ? preview.pages.map((pg, idx) => ({
          idx,
          layoutKey: idx === 0 ? 'carousel-cover' : 'carousel-tip',
          align: 'start' as const,
          textSize: 'm' as const,
          slots: { headline: pg.heading || undefined, body: pg.body || undefined, logo: true },
        }))
      : [
          {
            idx: 0,
            layoutKey: preview.layoutKey || 'text-card',
            align: 'start' as const,
            textSize: 'm' as const,
            slots: {
              headline: preview.title || undefined,
              body: preview.body || undefined,
              cta: preview.ctas[0] ?? undefined,
              hashtags: preview.hashtags,
              logo: true,
            },
          },
        ];
  return {
    channel: campaignChannelToPublish(preview.channel),
    contentType: 'post',
    title: preview.title,
    bodyText: preview.body,
    hooks: preview.hooks,
    layoutKey: preview.layoutKey || (format === 'carousel' ? 'carousel-cover' : 'text-card'),
    visualBrief: {
      brief: preview.visualDirection,
      stylePreset: '',
      aspect,
      imagePrompt: '',
      format,
      pages,
    },
    aiProvenance: provenance
      ? {
          capability: provenance.capability || 'campaign_spec',
          model: provenance.model,
          prompt_version: provenance.prompt_version,
          generated_at: provenance.generated_at,
        }
      : null,
  };
}
